import { useRef } from "react";
import { IconPencil } from "../control/ui/icons";
import { useTileScale } from "./useTileScale";

/**
 * What the click does, said out loud — the pencil and the words, centred on a hovered
 * region. The gesture predates the label by a long way (a click opens this tile's feed
 * popup) and nothing on the glass ever admitted it: the tile reads as plain video, and
 * the selection ring says only "this one", not "and it opens".
 *
 * DESKTOP ONLY, by media query rather than by the tap contract's `touchPointer` ref. A
 * finger's tap synthesises the very mouseenter that lights `hovered`, so a JS gate would
 * be re-deriving what `pointer-fine` already knows — and a hint that says "click" is for
 * the pointer that clicks. A finger gets the double-tap contract instead, which no label
 * would teach anyway.
 *
 * NOT ON THE WALL, where the same click buys the square instead of editing it. The host
 * mounts this on the same `!wallMode` condition as RegionHoverChrome on purpose: the hint
 * and the editing controls are one promise, and a wall makes neither.
 *
 * A FILE OF ITS OWN, and not because it is big. DisplayApp may not import a control glyph
 * — IconPencil among them — since that is the signature of a second control cluster
 * growing back beside the bar, and site-bar.mts holds the line. Living here it keeps the
 * bar's rule intact and joins the other per-region chrome (RegionHoverChrome,
 * WallRegionChrome, RegionFapOverlay), which are each a file too.
 *
 * Inert and centred — the layering contract has already spoken for the corners (crown
 * bottom-right at z-30, hover controls top-right), and pointer-events-none so it never
 * eats the click it advertises, the same rule the selection ring follows.
 *
 * SIZED BY THE TILE, through the same useTileScale the hover chrome and the fap crown
 * grow by. A region is anything from a sliver of a dense board to half a television, and
 * a fixed 11px would read as lint on the big one and swamp the small one. Sharing the
 * rule rather than inventing a second one is what keeps the three growing together.
 */
export function RegionEditHint() {
  const measureRef = useRef<HTMLDivElement>(null);
  const scale = useTileScale(measureRef);
  return (
    <div
      ref={measureRef}
      className="pointer-events-none absolute inset-0 z-20 hidden items-center justify-center pointer-fine:flex"
    >
      {/* Scaled about its own centre — no transformOrigin needed, and no inverse-width
          trick either: this is a pill that shrinks to its text, not a full-width label
          row that has to keep truncating at the tile's edge (cf. RegionTileLabels). */}
      <span
        className="flex items-center gap-1.5 rounded-full bg-black/45 px-3 py-1.5 text-[11px] font-medium tracking-wide text-white/75 backdrop-blur-[2px]"
        style={{ transform: `scale(${scale})` }}
      >
        <IconPencil width={12} height={12} />
        Click to Edit
      </span>
    </div>
  );
}
